/* eslint-disable jsx-a11y/control-has-associated-label */
import React from 'react'
import Image from 'next/image'

import { Column, Columns, Layout, Row, Rows, Section } from '@/components/layout'
import { Link, Text, Button } from '@/components/common'
import styles from '@/components/Pages/TemplatePages.module.scss'
import routes from '@/routes'
import invoiceTemplateImg from '@/assets/invoice-template/invoice-template.jpeg'
import invoicesReminderImg from '@/assets/invoice-template/invoices-reminder.jpeg'
import invoicesDiscountFeesImg from '@/assets/invoice-template/invoices-discount-fees.jpeg'
import invoicesBrandingImg from '@/assets/invoice-template/invoices-branding.jpeg'
import TemplateDownloadForm from '@/components/blocks/TemplateDownloadForm'
import { NEAT_DATA_URL } from '@/config'
import { Check, X } from '@/components/blocks/CompareTable'

const downloadLink = `${NEAT_DATA_URL}/documents/Neat%20Invoice%20&%20Payment%20Request%20Templates.zip`

const pageId = 'invoiceTemplate'

const getMessageId = id => `pages.invoiceTemplate.${id}`

const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' })

export default function InvoiceTemplate() {
  return (
    <Layout.Content>

      <Section bgBrandTertiary wrapperClassName="bgcolor-gradient">
        <Row center>
          <Column maxWidth={700} textCenter>
            <h1>Easy-to-use free payment request & invoice templates</h1>
            <p>What’s more rewarding than doing great work and getting paid?</p>
            <p>Our payment request templates are messages curated by us from our <Link messageId={getMessageId('blogPost')} to="/blog/how-to-ask-for-payment-politely" />. The free invoice template is blank and ready for your branding. The template will automatically calculate your line item totals, taxes, and then sum it all up for you.</p>
            <p>One quick save and your templates will be email ready.</p>
            <TemplateDownloadForm
              downloadLink={downloadLink}
              templateType="invoice"
            />
          </Column>
        </Row>
      </Section>
      <Section bgBrand wrapperClassName={styles.tryNeatInvoicing}>
        <Row center>
          <Column maxWidth={700} textCenter>
            <h2>Try Neat's Invoicing Tool</h2>
            <p>Invoicing + File Document/Receipt Management + Financial Reporting</p>
            <Link
              messageId={getMessageId('tryNeatTool.button')}
              size="xlarge"
              to={`${routes.signUp}?sku=neataiyr`}
              variant="success"
            />
            <Text.P onTint size="small">Neat's full suite platform offers a 30-day money back guarantee</Text.P>
          </Column>
        </Row>

      </Section>
      <Section>
        <Rows className={styles.subsectionHeader}>
          <h2>How to use this invoice template</h2>
          <p>Every cell in this invoice template is editable. Avoid modifying any field that has a formula to ensure proper tax and total calculations.</p>
        </Rows>
        <Columns className={styles.steps}>
          <Column size={33}>
            <h3>Step 1</h3>
            <p>Insert all your business information in the top right corner. Click the “Insert your logo” box to change the picture to your logo.</p>
            <p>On the left side, insert your customer’s information.</p>
          </Column>
          <Column size={33}>
            <h3>Step 2</h3>
            <p>Enter your billable items. If you bill hourly, enter the number of hours under “QUANTITY” and then your hourly rate.</p>
            <p>For project, milestone, or product billing, enter the cost of the item sold in the price column and how many that your customer purchased in the “QUANTITY” column.</p>
          </Column>
          <Column size={33}>
            <h3>Step 3</h3>
            <p>Add the tax rate in the last column for your billables so the template can calculate the total amount with tax for your line items.</p>
            <p>The template will total every line item in the “TOTAL” section.</p>
          </Column>
          <Column size={33}>
            <h3>Step 4</h3>
            <p>Add any note you wish to send to your customer in the “NOTES” section. Perhaps a thank you message and a list of accepted forms of payment.</p>
          </Column>
          <Column size={33}>
            <h3>Step 5</h3>
            <p>When you’re ready, save your template, name it something descriptive with a timestamp, your business name, and your client’s business name (e.g. ConsultingInvoice 10-14-21 Neat ABCCompany).</p>
            <p>Attach the invoice to an email and send! If you use <Link to="/invoicing/">Neat’s Invoicing</Link>, this part is done for you.</p>
          </Column>
        </Columns>

        <div className="text-center columns gap-18">
          <Image
            alt="Invoice Template"
            className="shadow-card"
            src={invoiceTemplateImg}
            style={{ marginBottom: '64px', height: 'auto' }}
          />
          <Rows>
            <div>
              <Button messageId={getMessageId('download')} onClick={scrollToTop} size="large" variant="primary" />
            </div>
          </Rows>
        </div>

      </Section>
      <Section bgSecondary>
        <Rows className={styles.subsectionHeader}>
          <h2>Neat – Even easier than invoice templates</h2>
          <p><Link to="/invoicing/">Neat</Link> is an invoice tool that not only sends invoices directly to your customers but also has a ton of features that help you get paid and save you time.</p>
        </Rows>
        <Row gap={32}>
          <Column className={styles.imgTextColumn}>
            <Image
              alt="Reminders"
              className="shadow-card"
              src={invoicesReminderImg}
              style={{ height: 'auto' }}
            />
            <Rows gap={0}>
              <h3>Invoice reminders</h3>
              <p>Send professional reminders to your customers to ensure you get paid on time.</p>
            </Rows>
          </Column>
          <Column className={styles.imgTextColumn}>
            <Image
              alt="Discounts"
              className="shadow-card"
              src={invoicesDiscountFeesImg}
              style={{ height: 'auto' }}
            />
            <Rows gap={0}>
              <h3>Discounts & tax calculation</h3>
              <p>Add tax or discounts to your services and products quickly.</p>
            </Rows>
          </Column>
          <Column className={styles.imgTextColumn}>
            <Image
              alt="Custom Branding"
              className="shadow-card"
              src={invoicesBrandingImg}
              style={{ height: 'auto' }}
            />
            <Rows gap={0}>
              <h3>Custom branding</h3>
              <p>Make your invoice represent your company with custom colors, logos, and branding.</p>
            </Rows>
          </Column>
        </Row>
      </Section>
      <Section>
        <Rows className={styles.subsectionHeader}>
          <h2>Invoice templates vs. Neat</h2>
        </Rows>
        <div className={styles.table}>
          <table className="shadow-card" width="100%">
            <thead>
              <tr>
                <th className="center">&nbsp;</th>
                <th className="center">Invoice Template</th>
                <th className="center">Neat</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className={styles.title}>Direct emailing to clients</td>
                <td><X /></td>
                <td><Check /></td>
              </tr>
              <tr>
                <td className={styles.title}>Invoice reminders</td>
                <td><X /></td>
                <td><Check /></td>
              </tr>
              <tr>
                <td className={styles.title}>View invoice receipts</td>
                <td><X /></td>
                <td><Check /></td>
              </tr>
              <tr>
                <td className={styles.title}>Send invoices from a mobile device</td>
                <td><X /></td>
                <td><Check /></td>
              </tr>
            </tbody>
          </table>
        </div>
      </Section>
      <Section paddingTop={0}>
        <Row>
          <Column alignCenter textCenter>
            <Text.H2 messageId={getMessageId('featuresFooter.header')} />
            <Text.P messageId={getMessageId('featuresFooter.text')} />
            <Link
              messageId={getMessageId('featuresFooter.button')}
              size="xlarge"
              to={`${routes.signUp}?sku=neataiyr`}
              variant="success"
            />
            <Text.P messageId="featuresFooter.smallText" size="small" />
          </Column>
        </Row>
      </Section>

    </Layout.Content>
  )
}

InvoiceTemplate.layoutProps = {
  pageId,
}
